import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { InactiveTimerService } from '../../../services/inactive-timer.service';
import { BaseStepService } from '../../general/services/base-step.services';
import { ShopSettingsService } from '../../general/services/shop-settings.service';
import { LoginStateService } from '../../login/services/login-state.service';
import { PartnerStateService } from '../../partner/services/partner-state.service';
import { PartnerCodeStepEnum } from '../models/partner-code-step.enum';
import { PartnerCodeStateService } from './partner-code-state.service';

@Injectable()
export class PartnerCodeInfoStepService extends BaseStepService<PartnerCodeStepEnum>{

  private _isLogoutProcess: boolean;

  constructor(
    protected logger: LoggerService,
    protected inactiveTimerService: InactiveTimerService,
    protected shopSettingsService: ShopSettingsService,
    protected partnerStateService: PartnerStateService,
    protected partnerCodeStateService: PartnerCodeStateService,
    private router: Router,
    private loginStateService: LoginStateService,
  ) {
    super(
      logger,
      inactiveTimerService,
      shopSettingsService,
      partnerStateService,
      partnerCodeStateService
    );
    this._steps = [
      PartnerCodeStepEnum.Scan,
      PartnerCodeStepEnum.ProfileInfo,
    ];
  }

  protected get serviceName(): string {
    return PartnerCodeInfoStepService.name;
  }

  protected get initStep(): PartnerCodeStepEnum {
    return PartnerCodeStepEnum.Init;
  }

  protected get resetStep(): PartnerCodeStepEnum {
    return PartnerCodeStepEnum.Init;
  }

  private _showPartnerCodeScanComponent(): void {
    this.router.navigate(['partner-code/scan']);
  }

  private _showPartnerCodeProfileInfoComponent(): void {
    this.router.navigate(['partner-code/transactions']);
  }

  private _initializeBeforeFirstStep(): void {
    this._stepIndex = -1;
    this._isLogoutProcess = false;
    this.forward();
  }

  private _showPartnerCodeRootComponent(): void {
    this._step = null;
    this._stepIndex = -1;
    this.router.navigate(['']);
  }

  isAvailableStep(step: PartnerCodeStepEnum): boolean {
    if (!step) {
      return false;
    }

    switch (step) {
      case PartnerCodeStepEnum.ProfileInfo:
        return this.isPartnerCodeProfileInfoAvailable;
      case PartnerCodeStepEnum.Scan:
        return this.isPartnerCodeScanAvailable;
      default:
        return true;
    }
  }

  update(
    step: PartnerCodeStepEnum = PartnerCodeStepEnum.Init,
  ): void {

    this._step = step;
    if (this.shopSettingsService.isVUMode) {
      this.inactiveTimerService.resetTimer();
    }

    switch (this._step) {
      case PartnerCodeStepEnum.Init:
        this._initializeBeforeFirstStep();
        break;
      case PartnerCodeStepEnum.Scan:
        this._logoutIfNeedBeforeCallMethod(this._showPartnerCodeScanComponent.bind(this));
        break;
      case PartnerCodeStepEnum.ProfileInfo:
        this._showPartnerCodeProfileInfoComponent();
        break;
      case PartnerCodeStepEnum.Reset:
        this._showPartnerCodeRootComponent();
        break;
      default:
        this.router.navigateByUrl('');
        break;
    }
  }

  back(): void {
    if (this._isLogoutProcess) {
      return;
    }

    if (this.prevStep()) {
      return;
    }

    switch (this._step) {
      case PartnerCodeStepEnum.Init:
      case PartnerCodeStepEnum.Scan:
      case PartnerCodeStepEnum.Reset:
      case PartnerCodeStepEnum.ProfileInfo:
        this.update(PartnerCodeStepEnum.Reset);
        break;
    }
  }

  get isPartnerCodeScanAvailable(): boolean {
    switch (this._step) {
      case PartnerCodeStepEnum.Reset:
        return false;
    }
    return this.shopSettingsService.isVUMode;
  }

  get isPartnerCodeProfileInfoAvailable(): boolean {
    return this.loginStateService.isLoggedIn;
  }

  private _logoutIfNeedBeforeCallMethod(method: () => void): void {
    if (!method) {
      return;
    }
    this._isLogoutProcess = true;
    if (this.isVUPartnerCodeAuthorization) {
      this.loginStateService.logout().subscribe(
        _ => {
          this._isLogoutProcess = false;
          method();
        });
    } else {
      this._isLogoutProcess = false;
      method();
    }
  }

  get isVUPartnerCodeAuthorization(): boolean {
    return this.shopSettingsService.isVUMode && this.shopSettingsService.shopSettings.partnerCodeAuthorization;
  }
}
